import { useId, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import wordmarkUrl from '@/assets/brand/wordmark.png'

type Size = 'sm' | 'md' | 'lg'

/** Inline so it needs no request and no flash; coloured by `--brand-mark-*` tokens so it follows the theme. */
function Mark({ size, className }: { size: Size; className?: string }) {
  const gradient = useId()
  const box = { sm: 'size-7', md: 'size-9', lg: 'size-14' }[size]
  return (
    <svg viewBox="0 0 1024 1024" className={cn('shrink-0', box, className)} aria-hidden focusable="false">
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--brand-mark-1)' }} />
          <stop offset="1" style={{ stopColor: 'var(--brand-mark-2)' }} />
        </linearGradient>
      </defs>
      <path d="M482 130 L110 430 V880 a48 48 0 0 0 48 48 H482 Z" fill={`url(#${gradient})`} />
      <path d="M542 130 L914 430 V880 a48 48 0 0 1 -48 48 H542 Z" style={{ fill: 'var(--brand-mark-3)' }} />
    </svg>
  )
}

/**
 * The brand logotype, as on every other surface (LP-547): the design system's wordmark without its tagline, drawn as a
 * mask filled with the current text colour so it follows light, dark and the brand panel the way the name in a font did.
 * About half the mark's height, as in the app's lockup.
 */
function Wordmark({ size }: { size: Size }) {
  const height = { sm: 'h-3.5', md: 'h-4.5', lg: 'h-7' }[size]
  return <span role="img" aria-label="LeazePro" data-wordmark className={cn('brand-wordmark inline-block aspect-[800/189] shrink-0', height)} style={{ '--wordmark': `url(${wordmarkUrl})` } as CSSProperties} />
}

// Decorative: the mark is hidden from assistive tech, so screen readers hear "LeazePro" once at most.
export function Brand({ size = 'md', variant = 'lockup', className }: { size?: Size; variant?: 'lockup' | 'mark'; className?: string }) {
  if (variant === 'mark') return <Mark size={size} className={className} />
  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <Mark size={size} />
      <Wordmark size={size} />
    </div>
  )
}
